import { StandardTextFieldProps } from "@mui/material"
import { memo } from "react"
import { WidthOpt } from "../../assets/inputStyles"
import { EmployeeType } from "../../store/types/MissionsTypes"
import { prettyJson } from "../../tools"
import { NexAutocomplete } from "./Autocomplete"

interface EmployeeFieldProps extends StandardTextFieldProps {
    employeeId: string | undefined
    employeeList: {
        employees: EmployeeType[]
        project_employees: {
            pilots: string[]
            operators: string[]
            managers: string[]
            reviewers: string[]
        }
        nexus_employees: {
            pilots: string[]
            operators: string[]
            managers: string[]
            reviewers: string[]
        }
    }
    employeeRole: "pilots" | "operators" | "managers" | "reviewers"
    handleChange: (employeeId: string | undefined) => void
    required?: boolean
    label?: string
    width?: WidthOpt
    fullWidth?: boolean
    disabled?: boolean
}

export const EmployeeField = memo(
    ({ required, employeeId, employeeRole, employeeList, handleChange, fullWidth, ...props }: EmployeeFieldProps) => {
        const { employees, project_employees, nexus_employees } = employeeList
        const value = employees.find((emp: EmployeeType) => emp.employee_id === employeeId) ?? null

        const inMissions = (id: string, arr: string[]) => arr.some((uniqueID) => uniqueID === id)
        const inProject = (id: string) => inMissions(id, project_employees[employeeRole] ?? [])
        const inNexus = (id: string) => inMissions(id, nexus_employees[employeeRole] ?? [])

        const options = [...employees].sort((a, b) => {
            const idA = a.employee_id
            const idB = b.employee_id

            const inProjectA = idA && inProject(idA)
            const inProjectB = idB && inProject(idB)

            if (inProjectA) {
                if (inProjectB) {
                    return 0
                }
                return -1
            }

            const inNexusA = idA && inNexus(idA)
            const inNexusB = idB && inNexus(idB)

            if (inNexusA) {
                if (inProjectB || inNexusB) {
                    return 0
                }
                return -1
            }
            return 0
        })

        const groupBy = (opt: EmployeeType) => {
            if (opt.employee_id == null) {
                throw new Error(`No employee id for employee: ${[prettyJson(opt)]}`)
            }
            const id = opt.employee_id

            if (inProject(id)) {
                return "Used in This Project"
            }
            if (inNexus(id)) {
                return "Used in Other Projects"
            }
            return "All Employees"
        }

        return (
            <NexAutocomplete
                fullWidth={fullWidth}
                value={value}
                options={options}
                getOptionLabel={(emp: EmployeeType) => emp.employee_full_name}
                renderOption={(props, emp) => (
                    <li {...props} key={emp.employee_id}>
                        {emp.employee_full_name}
                    </li>
                )}
                groupBy={groupBy}
                onChange={(_, value) => handleChange(value?.employee_id ?? undefined)}
                TextFieldProps={{ required, error: required && !value, ...props }}
            />
        )
    },
)
